import { Phone } from 'lucide-react';
import {
  CampaignAccessDenied,
  CampaignPageShell,
  CampaignTabs,
} from '@/client/components/admin/Campaigns/CampaignShell';
import CampaignSendForm from '@/client/components/admin/Campaigns/CampaignSendForm';
import CampaignSendHistory from '@/client/components/admin/Campaigns/CampaignSendHistory';
import CampaignSuppressionsPanel from '@/client/components/admin/Campaigns/CampaignSuppressionsPanel';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';

const SMS_TABS = ['send', 'history', 'suppressions'] as const;
type Tab = (typeof SMS_TABS)[number];

const SMSPage = () => {
  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/sms?tab=send' },
    { label: 'SMS' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const sendPerm = useHasPermission('messaging.send');
  const suppressPerm = useHasPermission('messaging.suppress');
  const overridePerm = useHasPermission('messaging.override');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canSend = sendPerm.isSuperAdmin || sendPerm.hasPermission;
  const canSuppress = suppressPerm.isSuperAdmin || suppressPerm.hasPermission;
  const canOverride = overridePerm.isSuperAdmin || overridePerm.hasPermission;
  const permLoading = viewPerm.isLoading || sendPerm.isLoading;

  const [tab, setTab] = useTabSearchParam<Tab>(SMS_TABS, 'send');
  const tabs = [
    ...(canSend ? [{ id: 'send' as const, label: 'Compose' }] : []),
    ...(canView ? [{ id: 'history' as const, label: 'Send history' }] : []),
    ...(canView || canSuppress
      ? [{ id: 'suppressions' as const, label: 'Suppressions' }]
      : []),
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id ?? 'history';

  if (permLoading) {
    return (
      <CampaignPageShell
        loading
        icon={<Phone className="h-4 w-4 text-white" />}
        title="SMS"
        description=""
      >
        {null}
      </CampaignPageShell>
    );
  }

  if (!canView && !canSend && !canSuppress) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <CampaignAccessDenied code="messaging.view" />
        </div>
      </div>
    );
  }

  return (
    <CampaignPageShell
      icon={<Phone className="h-4 w-4 text-white" />}
      title="SMS"
      description="Ad-hoc SMS via messaging v2, send history, and phone suppressions (STOP / bounces)."
    >
      <CampaignTabs tabs={tabs} active={activeTab} onChange={setTab} />

      {activeTab === 'send' && canSend && (
        <CampaignSendForm defaultChannels={['sms']} lockedChannels canOverride={canOverride} />
      )}
      {activeTab === 'history' && canView && <CampaignSendHistory channel="sms" />}
      {activeTab === 'suppressions' && (canView || canSuppress) && (
        <CampaignSuppressionsPanel canWrite={canSuppress} />
      )}
    </CampaignPageShell>
  );
};

export default SMSPage;
